import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { EventType, type AuthenticationResult } from '@azure/msal-browser';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { msalInstance } from '@core/auth/msal.config';
import { configureAgGrid } from '@core/config/ag-grid.config';
import { loadDeploymentFlags } from '@core/state/feature-flags.store';
import { loadSettings } from '@core/state/settings.store';
import { persistUserToCache, seedUserFromCache } from '@core/state/user-cache';

/*
 * Before `@app/App`, and that order is load-bearing.
 *
 * styles.scss pulls in _ui-primitives.scss, the base layer under the `cw-*`
 * components in src/ui, and every feature stylesheet is written as an override
 * of it. Imported the other way round its defaults are emitted *after* the
 * sheets meant to override them and win on source order at equal specificity:
 * `.cw-drawer { background-color: #fff }` beats `.left-sidenav`'s indigo,
 * leaving the brand rail white with its near-white labels on top, and
 * `.cw-drawer-container`'s canvas beats the shell's --app-content-bg. Base
 * first, overrides after.
 *
 * Tailwind comes last. Its theme and utilities sit in cascade layers, so an
 * unlayered rule in any of the SCSS above beats them whatever the order - a
 * utility can never silently repaint a feature screen.
 */
import './styles.scss';
import './tailwind.css';

import { RequireAuth } from '@app/RequireAuth';
import App from '@app/App';

/**
 * Bootstrap.
 *
 * `initialize()` is awaited before the first render. MSAL v3+ requires it to
 * have resolved before any other API call, and rendering first means the
 * router's auth guard runs against an uninitialised client.
 *
 * The deployment's feature flags and the organisation settings are read
 * alongside it and awaited too, so a feature switched off - on the server or
 * at the user's scope - is never drawn and then withdrawn. Both loads are
 * time-boxed and fail open to the shipped defaults.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The portal's data is refreshed explicitly - by SignalR, by grid
      // refreshes and by the user - so background refetching would only
      // duplicate work and surprise people mid-edit.
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000
    }
  }
});

// Must run before any lazily loaded screen creates a grid.
configureAgGrid();

async function bootstrap(): Promise<void> {
  const flagsLoaded = Promise.all([loadDeploymentFlags(), loadSettings()]);

  await msalInstance.initialize();

  // Before the first render, so a redirect return has an active account to find.
  await msalInstance.handleRedirectPromise();

  const accounts = msalInstance.getAllAccounts();

  if (!msalInstance.getActiveAccount() && accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  // A fresh sign-in has to become the active account, or every later token
  // request has nothing to use.
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      msalInstance.setActiveAccount((event.payload as AuthenticationResult).account);
    }
  });

  // Keeps ACCOUNT_ADDED / ACCOUNT_REMOVED in sync across tabs.
  msalInstance.enableAccountStorageEvents();

  // The shell draws from the last resolved user while the toolbar re-resolves
  // it, so the nav is there on the first frame rather than after Graph answers.
  seedUserFromCache(msalInstance.getActiveAccount()?.localAccountId);
  persistUserToCache();

  await flagsLoaded;

  const container = document.getElementById('root');

  if (!container) {
    throw new Error('Root container #root is missing from index.html');
  }

  createRoot(container).render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            {/* Outside the shell, so none of App's effects, route resolution or
                cached-record reads run for a visitor who is not signed in yet. */}
            <RequireAuth>
              <App />
            </RequireAuth>
          </BrowserRouter>
        </QueryClientProvider>
      </MsalProvider>
    </StrictMode>
  );
}

void bootstrap();
